// client/src/pages/MyModelsPage.js
// The models a shopper owns, used to find parts that fit them.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FlightOutlinedIcon from '@mui/icons-material/FlightOutlined';
import EmptyState from '../components/EmptyState';
import { API_BASE_URL } from '../utils/media';
import { getMyModels, saveMyModels } from '../utils/productSpecs';

const MyModelsPage = () => {
  const [models, setModels] = useState(() => getMyModels());
  const [known, setKnown] = useState([]);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/products/compatible-models`)
      .then((res) => (res.ok ? res.json() : []))
      .then(setKnown)
      .catch(() => {});
  }, []);

  const update = (next) => {
    setModels(next);
    saveMyModels(next);
  };

  const add = (e) => {
    e.preventDefault();
    const name = draft.trim();
    if (!name) return;
    if (!models.some((model) => model.toLowerCase() === name.toLowerCase())) update([...models, name]);
    setDraft('');
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>My Models</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Add what you fly or build, and we'll show you which parts fit. Your list is saved on this device.
      </Typography>
      <Stack component="form" onSubmit={add} direction="row" spacing={1} sx={{ mb: 3, maxWidth: 520 }}>
        <Autocomplete
          freeSolo
          fullWidth
          size="small"
          options={known}
          inputValue={draft}
          onInputChange={(e, value) => setDraft(value)}
          renderInput={(params) => <TextField {...params} label="Model name" />}
        />
        <Button type="submit" variant="contained" startIcon={<AddIcon />} sx={{ flexShrink: 0 }}>Add</Button>
      </Stack>
      {models.length === 0 ? (
        <EmptyState icon={<FlightOutlinedIcon />} title="No models yet" description="Add your first model above." />
      ) : (
        <Stack spacing={1}>
          {models.map((model) => (
            <Stack key={model} direction="row" alignItems="center" spacing={1} sx={{ p: 1.5, border: 1, borderColor: 'divider' }}>
              <Typography fontWeight={600} sx={{ flexGrow: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{model}</Typography>
              <Button size="small" component={Link} to={`/?${new URLSearchParams({ fits: model })}`} sx={{ flexShrink: 0 }}>Parts that fit</Button>
              <IconButton size="small" aria-label={`Remove ${model}`} onClick={() => update(models.filter((m) => m !== model))}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default MyModelsPage;
